import React from "react";
import {
  Box,
  Typography,
  Checkbox,
  Popper,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

const DraftCollectionPopper = ({
  anchorEl,
  popoverOpen,
  draftCollections,
  selectedDrafts,
  onDraftClick,
  onCreateCollection,
  tr,
}) => {
  const id = popoverOpen ? "simple-popper" : undefined;
  const [creating, setCreating] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  // With no collection there is nothing to pick, so creating one is the whole
  // point of having opened this — offer the form straight away instead of the
  // old dead end that just said "No drafts".
  const isEmpty = !draftCollections.length;
  const showForm = creating || isEmpty;

  const submit = async () => {
    const title = newTitle.trim();
    if (!title || !onCreateCollection) return;
    setBusy(true);
    await onCreateCollection(title);
    setBusy(false);
    setNewTitle("");
    setCreating(false);
  };

  return (
    <Popper
      sx={{ zIndex: 3000, pointerEvents: "auto" }}
      id={id}
      open={popoverOpen}
      anchorEl={anchorEl}>
      <Box
        sx={{
          p: 1,
          bgcolor: "var(--background-menu-list)",
          borderRadius: "10px",
          pointerEvents: "auto",
          minWidth: 280,
        }}>
        <Typography
          sx={{
            padding: 1,
            fontSize: 16,
            color: "#73E2E0",
            fontWeight: "500",
          }}>
          {tr("postPage.saveToCollection")}
        </Typography>
        <Box className="flex column gap4">
          {draftCollections.map((info) => {
            const isChecked = selectedDrafts.includes(info.id);
            return (
              // The whole row is the target now: clicking the title did nothing
              // before, which made the popover look broken.
              <Box
                key={info.id}
                className="flex between centerV"
                onClick={() => onDraftClick(info)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "rgba(115,226,224,0.08)" },
                }}>
                <Typography
                  sx={{
                    padding: 1,
                    fontSize: 14,
                    color: "var(--text-color)",
                    fontWeight: "500",
                  }}>
                  {info.title}
                </Typography>
                <Checkbox
                  icon={<RadioButtonUncheckedIcon sx={{ color: "white" }} />}
                  checkedIcon={
                    <RadioButtonCheckedIcon sx={{ color: "#73E2E0" }} />
                  }
                  checked={isChecked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onDraftClick(info)}
                />
              </Box>
            );
          })}

          {isEmpty && (
            <Typography
              sx={{ px: 1, fontSize: 13, color: "var(--text-color)", opacity: 0.7 }}>
              {tr("postPage.noCollectionsHint")}
            </Typography>
          )}

          {onCreateCollection && showForm && (
            <Box className="flex centerV gap8" sx={{ p: 1 }}>
              <TextField
                size="small"
                autoFocus={creating}
                placeholder={tr("postPage.newCollectionPlaceholder")}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                inputProps={{ maxLength: 255 }}
                sx={{ flex: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={busy || !newTitle.trim()}
                onClick={submit}
                startIcon={busy ? <CircularProgress size={14} /> : <AddIcon />}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                {tr("postPage.createAndSave")}
              </Button>
            </Box>
          )}

          {onCreateCollection && !showForm && (
            <Button
              size="small"
              onClick={() => setCreating(true)}
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", justifyContent: "flex-start" }}>
              {tr("postPage.newCollection")}
            </Button>
          )}
        </Box>
      </Box>
    </Popper>
  );
};

export default DraftCollectionPopper;
